import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductShareLink, { ProductShareLinkStyles } from "@MasterBluePrint/Overrides/Components/ProductShareLink";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    shareLink?: ProductShareLinkStyles;
    brand?: InjectableCss;
    pbBottom?: InjectableCss;
    brandGridItemright?: InjectableCss;
    productBrandRightLogo?: InjectableCss;
    mt2?: InjectableCss;
    brandGridContainerInnerright?: GridContainerProps;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 9.375rem;
                    max-height: 9.375rem;
                    padding:0;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        css: css`
              font-size: 1.5rem;
              color:#002554;  
              font-weight: 600 !important;
              margin-top:1.2rem;
              line-height: 1.8rem;
            `,
        variant: "h1",
    },
    brand: {
        css: css`
        text-transform: uppercase;
        fontFamily: "'Roboto', sans-serif",
        color:#002554;
        font-size:.88rem;
        font-weight:600;
         }
        `,
    },
    pbBottom: {
        css: css`
            padding-bottom:0 !important;
         }
        `,
    },
    productBrandRightLogo:{
        css: css`
        padding-top:0 ;
     }
    `,
    },
    brandGridItemright: {
        css: css`
        justify-content: flex-end;
       > div > div:nth-child(1) {
            justify-content: flex-end;
            width: 100%;
        }
         > div > div:nth-child(1) > div > div > div {
            justify-content: flex-end;
        }
         > div > div:nth-child(2) > div > div > div {
            justify-content: flex-end;
        }
         > div > div {
            padding-top:0;
            padding-bottom:0;
                }

        div[data-test-selector="product-brand-logo"] {
            .brand-name {
                min-height: 6.6875rem;
            }
        }
        `,

    },
    mt2: {
        css: css`
        padding-top:1.5rem;
         }
        `,
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product }) => {
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.brandGridItem}  {...styles.brandGridItemright} data-test-selector="brand-item-right">
                <GridContainer  {...styles.brandGridContainerInnerright} >
                    {product.brand && (
                        <GridItem width={8} {...styles.productBrandRightLogo} data-test-selector="product-brand-logo">
                            <ProductBrand
                                brand={product.brand}
                                showLogo={true}
                                extendedStyles={styles.brandStyles}
                                {...styles.pbBottom}
                            />
                        </GridItem>
                    )}
                    <GridItem width={4}>
                        <GridContainer gap={2}>
                            <GridItem width={6} {...styles.mt2} >
                                <ProductShareLink extendedStyles={styles.shareLink}></ProductShareLink>
                            </GridItem>
                            <GridItem width={6} {...styles.mt2}>
                                <Clickable onClick={openPrintDialog} data-test-selector="productDetails_print" aria-label="Print Product">
                                    <Icon src="Printer" />
                                </Clickable>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>

            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText} data-test-selector="ProductDetailsPageTitle">
                    {product.productTitle}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPageTitle),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
